import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartErrorEvent, CartNoteUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  #saving = false;
  #pendingNote = null;

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = debounce((event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    this.#pendingNote = { note: event.target.value, event };
    this.#showError(false);
    return this.#savePendingNotes();
  }, 200);

  async #savePendingNotes() {
    if (this.#saving) return;
    this.#saving = true;

    try {
      while (this.#pendingNote) {
        const { note, event } = this.#pendingNote;
        this.#pendingNote = null;
        const deferredPromise = CartNoteUpdateEvent.createPromise();
        deferredPromise.promise.catch(() => {});

        this.dispatchEvent(
          new CartNoteUpdateEvent({
            context: this.closest('dialog') ? 'dialog' : 'cart',
            note,
            promise: deferredPromise.promise,
          })
        );

        try {
          const response = await fetch(
            Theme.routes.cart_update_url,
            fetchConfig('json', { body: JSON.stringify({ note }) })
          );
          if (!response.ok) throw new Error(`Cart note update failed: ${response.status}`);
          const data = await response.json();
          deferredPromise.resolve({ cart: CartNoteUpdateEvent.createCartFromAjaxResponse(data) });
          this.#showError(false);
        } catch (error) {
          deferredPromise.reject(error);
          if (!this.#pendingNote) this.#showError(true);
          this.dispatchEvent(
            new CartErrorEvent({
              error: this.dataset.saveError || 'Failed to save cart note',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        } finally {
          cartPerformance.measureFromEvent('note-update:user-action', event);
        }
      }
    } finally {
      this.#saving = false;
    }
  }

  #showError(visible) {
    const error = this.querySelector('[data-note-error]');
    if (error instanceof HTMLElement) error.hidden = !visible;
  }
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
